import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux'
import { AuthTokens, tokenStore } from 'src/queries/tokenStore'
import { RootState } from 'src/redux/createRootReducer'

/**
 * Interop write-through: mirror the Redux auth token fields into the non-Redux
 * `tokenStore` (the single bridge for auth-redux-elimination). Redux remains the
 * WRITE source; `tokenStore` is the transport READ source.
 *
 * Wired only on the browser store (`configureStore(..., isClient=true)`). SSR does
 * NOT mirror a per-request Redux store into this process-level store; `src/server.ts`
 * seeds a request-scoped token context directly via `runWithTokenContext` so
 * concurrent requests cannot share a bearer. See the auth-redux-migration skill
 * reference for the SSR request-scoping STOP condition.
 */

/** The five auth-token fields Redux owns, read off the auth slice. */
const tokensFromState = (state: RootState): AuthTokens => {
  const { jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId } =
    state.auth
  return { jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId }
}

/**
 * Mirror the current Redux auth token fields into `tokenStore`. `setTokens` diffs,
 * so an unchanged state is a no-op (no snapshot churn, no listener notify).
 */
export const syncTokenStoreFromState = (state: RootState): void => {
  tokenStore.setTokens(tokensFromState(state))
}

/**
 * Prime `tokenStore` from the store's current (SSR-seeded, hydrated) auth state.
 * Call once on the browser entry after the store is created and before the first
 * authed request is built — the write-through middleware only fires on subsequent
 * dispatches, so without this the initial hydrated bearer would be missing.
 */
export const primeTokenStore = (store: {
  getState: () => RootState
}): void => {
  syncTokenStoreFromState(store.getState())
}

/**
 * Redux write-through middleware: after each action reduces, mirror the (possibly
 * changed) auth token fields into `tokenStore` so the transport read source stays
 * byte-exact with Redux.
 *
 * It reads post-reduce `state.auth` UNCONDITIONALLY rather than keying on an
 * action-type allowlist: ~20 reducer cases mutate these fields (signin, csp login,
 * renew, heartbeat, signout, emulation, get-me failure, …), so an allowlist would
 * silently drift the mirror on any un-enumerated flow. The diff inside
 * `tokenStore.setTokens` keeps this cheap — no snapshot replacement or listener
 * notify when the tokens are unchanged (the vast majority of actions).
 */
export const tokenStoreWriteThroughMiddleware: Middleware<
  Record<string, never>,
  RootState
> =
  (store: MiddlewareAPI<Dispatch<AnyAction>, RootState>) =>
  (next: Dispatch<AnyAction>) =>
  (action: AnyAction): unknown => {
    const result = next(action)
    syncTokenStoreFromState(store.getState())
    return result
  }
